import React from 'react';

const PartA2 = ({ data, pageNumber = 1 }) => {
    // Handle both page 1 and page 2 of Part A2
    const isPage1 = pageNumber === 1;

    if (isPage1) {
        return <PartA2Page1 data={data} />;
    } else {
        return <PartA2Page2 data={data} />;
    }
};

// Page 1: Self-Evaluation & School Goals
const PartA2Page1 = ({ data }) => {
    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            {/* Header */}
            <div className="text-center mb-4">
                <h2 className="text-xl font-bold text-[#F97316] mb-1">PART A (2)</h2>
                <h3 className="text-lg font-bold text-black">Self-Evaluation</h3>
            </div>

            {/* Performance Evaluation Table */}
            <div className="mb-4 border-2 border-black">
                <div className="border-b-2 border-black bg-[#DBEAFE] p-1 text-center font-bold text-xs">
                    Circle the most appropriate option.
                </div>
                <div className="flex">
                    <div className="w-1/2 border-r-2 border-black p-2 bg-[#DBEAFE]/30">
                        <p className="mb-2 font-bold text-xs">Last year, my performance at school was...</p>
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-red-500">← Poor</span>
                            <div className="flex-1 mx-2 relative">
                                <div className="w-full h-1.5 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"></div>
                                <div
                                    className="absolute top-[-2px] w-3 h-3 bg-blue-600 border border-white rounded-full"
                                    style={{ left: `${((data?.last_year_performance || 5) - 1) * 10}%` }}
                                ></div>
                            </div>
                            <span className="text-[10px] font-bold text-green-600">Excellent →</span>
                        </div>
                    </div>
                    <div className="w-1/2 p-2 bg-[#DBEAFE]/30">
                        <p className="mb-2 font-bold text-xs">My teachers thought my efforts last year were...</p>
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-red-500">← Poor</span>
                            <div className="flex-1 mx-2 relative">
                                <div className="w-full h-1.5 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full"></div>
                                <div
                                    className="absolute top-[-2px] w-3 h-3 bg-blue-600 border border-white rounded-full"
                                    style={{ left: `${((data?.teacher_thought_efforts || 5) - 1) * 10}%` }}
                                ></div>
                            </div>
                            <span className="text-[10px] font-bold text-green-600">Excellent →</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* New Things and Improvements */}
            <div className="mb-4 bg-[#DBEAFE]/30 flex">
                <div className="w-1/2 p-2 border-r border-white">
                    <label className="block font-bold text-xs mb-1">This year, I will try new things like...</label>
                    <div className="text-xs min-h-[3em]">{data?.try_new_things || ''}</div>
                </div>
                <div className="w-1/2 p-2">
                    <label className="block font-bold text-xs mb-1">I will work harder on things like...</label>
                    <div className="text-xs min-h-[3em]">{data?.work_harder_on || ''}</div>
                </div>
            </div>

            {/* Career Aspirations */}
            <div className="mb-4 border-2 border-[#2563EB] rounded overflow-hidden">
                <div className="flex border-b border-[#2563EB] bg-[#DBEAFE]">
                    <div className="w-1/2 p-1 text-center font-bold text-xs border-r border-[#2563EB]">My career aspirations is/are</div>
                    <div className="w-1/2 p-1 text-center font-bold text-xs">To fulfill my aspirations, I need to</div>
                </div>
                <div className="flex bg-[#DBEAFE]/20">
                    <div className="w-1/2 p-2 border-r border-[#2563EB]">
                        {(data?.career_aspirations || ['', '', '', '']).map((item, i) => (
                            <div key={i} className="flex text-xs mb-1">
                                <span className="font-bold text-[#2563EB] mr-1">{i + 1}.</span>
                                <span className="border-b border-gray-300 flex-1">{item}</span>
                            </div>
                        ))}
                    </div>
                    <div className="w-1/2 p-2">
                        {(data?.to_fulfill_aspirations || ['', '', '', '']).map((item, i) => (
                            <div key={i} className="flex text-xs mb-1">
                                <span className="font-bold text-[#2563EB] mr-1">{i + 1}.</span>
                                <span className="border-b border-gray-300 flex-1">{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* --- SCHOOL GOALS SECTION (Moved to Page 1) --- */}
            <div className="bg-[#E5E7EB] p-4 rounded relative mt-6">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <h2 className="text-lg font-bold text-[#F97316] bg-white px-3 py-0.5 rounded shadow-sm">Goals!</h2>
                </div>

                <h3 className="text-center font-bold text-sm mb-3 mt-1">My goal in school last year was...</h3>

                {/* Past Goal */}
                <div className="bg-gray-50 p-2 mb-2 border-l-4 border-gray-400">
                    <div className="text-xs mb-1"><span className="font-bold">Goal:</span> {data?.school_goal_last_year}</div>
                    <div className="text-xs">
                        <span className="font-bold">Status:</span> {data?.school_goal_status === 'accomplished' ? 'Accomplished' : data?.school_goal_status === 'still_working' ? 'Still Working' : ''}
                    </div>
                </div>

                {/* Importance */}
                <div className="bg-[#DBEAFE] p-2 mb-2 border-l-4 border-blue-400">
                    <div className="font-bold text-xs text-blue-800">Why was the goal important to you?</div>
                    <div className="text-xs">{data?.school_goal_importance}</div>
                </div>

                {/* New Goal */}
                <div className="bg-[#DBEAFE] p-2 mb-2">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <div className="font-bold text-xs text-blue-800 mb-1">ONE specific goal I would like to achieve in school by the end of this year:</div>
                            <div className="text-xs bg-white p-1 min-h-[2em]">{data?.school_goal_this_year}</div>
                        </div>
                        <div>
                            <div className="font-bold text-xs text-blue-800 mb-1">How will I know I have achieved this goal?</div>
                            <div className="text-xs bg-white p-1 min-h-[2em]">{data?.school_goal_achievement_indicator}</div>
                        </div>
                    </div>
                </div>

                {/* Action Steps */}
                <div className="bg-[#DBEAFE] p-2 mb-2">
                    <div className="font-bold text-xs text-blue-800 mb-1">To achieve this goal, things I need to do:</div>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-white p-1 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">A week from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.school_goal_week_action}</div>
                        </div>
                        <div className="bg-white p-1 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">6 weeks from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.school_goal_six_weeks_action}</div>
                        </div>
                        <div className="bg-white p-1 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">6 months from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.school_goal_six_months_action}</div>
                        </div>
                    </div>
                </div>

                {/* Support System */}
                <div className="bg-[#DBEAFE] border border-blue-200">
                    <div className="bg-blue-100 p-1 text-center font-bold text-xs text-blue-800 border-b border-blue-200">
                        Things that will help me achieve this goal
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-blue-200">
                        <div className="p-1">
                            <div className="font-bold text-[10px] text-center mb-1">My strengths/abilities</div>
                            {(data?.school_goal_strengths || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                        <div className="p-1">
                            <div className="font-bold text-[10px] text-center mb-1">Support I get at home/community</div>
                            {(data?.school_goal_home_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                        <div className="p-1">
                            <div className="font-bold text-[10px] text-center mb-1">Support I get at school</div>
                            {(data?.school_goal_school_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">2</div>
        </div>
    );
};

// Page 2: Outside School Goals
const PartA2Page2 = ({ data }) => {
    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            padding: '20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden',
            position: 'relative',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
        }}>
            {/* Orange corner triangles */}
            <div className="absolute top-0 right-0 w-0 h-0"
                style={{
                    borderTop: '80px solid #f1905b',
                    borderLeft: '80px solid transparent'
                }}>
            </div>
            <div className="absolute bottom-0 left-0 w-0 h-0"
                style={{
                    borderBottom: '80px solid #F97316',
                    borderRight: '80px solid transparent'
                }}>
            </div>

            {/* --- OUTSIDE SCHOOL GOALS SECTION --- */}
            <div className="bg-[#DBEAFE]/30 p-4 rounded relative mt-8 border-t-4 border-[#F97316]">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <h2 className="text-lg font-bold text-[#F97316] bg-white px-3 py-0.5 rounded shadow-sm border border-orange-100">Goals!</h2>
                </div>

                <h3 className="text-center font-bold text-sm mb-4 mt-2">My goal outside of school last year was...</h3>

                {/* Past Goal */}
                <div className="bg-white p-3 mb-3 border-l-4 border-gray-400 shadow-sm">
                    <div className="text-xs mb-1"><span className="font-bold">Goal:</span> {data?.outside_goal_last_year}</div>
                    <div className="text-xs">
                        <span className="font-bold">Status:</span> {data?.outside_goal_status === 'accomplished' ? 'Accomplished' : data?.outside_goal_status === 'still_working' ? 'Still Working' : ''}
                    </div>
                </div>

                {/* New Goal */}
                <div className="bg-[#DBEAFE] p-3 mb-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="font-bold text-xs text-blue-800 mb-1">ONE specific goal I would like to achieve outside of school by the end of this year:</div>
                            <div className="text-xs bg-white p-1 min-h-[3em]">{data?.outside_goal_this_year}</div>
                        </div>
                        <div>
                            <div className="font-bold text-xs text-blue-800 mb-1">How will I know I have achieved this goal?</div>
                            <div className="text-xs bg-white p-1 min-h-[3em]">{data?.outside_goal_achievement_indicator}</div>
                        </div>
                    </div>
                </div>

                {/* Action Steps */}
                <div className="bg-[#DBEAFE] p-3 mb-3">
                    <div className="font-bold text-xs text-blue-800 mb-2">To achieve this goal, things I need to do:</div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white p-2 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">A week from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.outside_goal_week_action}</div>
                        </div>
                        <div className="bg-white p-2 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">6 weeks from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.outside_goal_six_weeks_action}</div>
                        </div>
                        <div className="bg-white p-2 border border-blue-200">
                            <div className="font-bold text-[10px] text-blue-600 uppercase">6 months from now</div>
                            <div className="text-[10px] min-h-[3em]">{data?.outside_goal_six_months_action}</div>
                        </div>
                    </div>
                </div>

                {/* Support System */}
                <div className="bg-[#DBEAFE] border border-blue-200 mb-4">
                    <div className="bg-blue-100 p-1 text-center font-bold text-xs text-blue-800 border-b border-blue-200">
                        Things that will help me achieve this goal
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-blue-200">
                        <div className="p-2">
                            <div className="font-bold text-[10px] text-center mb-1">My strengths/abilities</div>
                            {(data?.outside_goal_strengths || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                        <div className="p-2">
                            <div className="font-bold text-[10px] text-center mb-1">Support I get at home/community</div>
                            {(data?.outside_goal_home_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                        <div className="p-2">
                            <div className="font-bold text-[10px] text-center mb-1">Support I get at school</div>
                            {(data?.outside_goal_school_support || ['', '', '']).map((item, i) => (
                                <div key={i} className="flex text-[10px] mb-0.5"><span className="font-bold mr-1">{i + 1}.</span>{item}</div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Importance */}
                <div className="bg-[#DBEAFE] p-3 mb-4 border-l-4 border-blue-400">
                    <div className="font-bold text-xs text-blue-800 mb-1">Why does this goal matter to you?</div>
                    <div className="text-xs min-h-[2em]">{data?.outside_goal_importance}</div>
                </div>

                {/* Achievements */}
                <div className="bg-[#FEF3C7] p-3 border-l-4 border-yellow-400">
                    <div className="font-bold text-xs text-yellow-800 mb-1">Some goals I have achieved till date and am proud of/happy with...</div>
                    <div className="text-xs min-h-[3em]">{data?.achieved_goals_proud_of}</div>
                </div>
            </div>

            {/* Page Number */}
            <div className="absolute bottom-3 left-0 right-0 text-center text-xs font-medium">3</div>
        </div>
    );
};

export default PartA2;